import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, InputLabel, Select } from '@components/Common';
import { FilterContainer } from '@components/Common/EntryFilters';
import DateFilterControlled from '@components/Common/EntryFilters/DateFilterControlled';
import FormLoader from '@components/Common/Loaders/FormLoader';
import NavBar from '@components/Pages/Reports/Common/ReportNavigation';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useDownloadBookkeepingEntriesReportMutation } from '@api/queries/reports';
import { useBookkeepingEntriesReportFilters } from '@hooks/useBookkeepingEntriesReportFilters';

// #region styles

const Columns = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  > div.column {
    box-sizing: border-box;
    width: 25%;
    padding-right: 15px;

    &.wide {
      flex-grow: 1;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

const Column = styled.div`
  width: 25%;
  margin-right: 20px;
`;

const CreateReportButton = styled(Button)`
  display: inline-block;
  padding: 10px 35px;
  text-decoration: none;

  border-right-width: 0;
  background: rgb(5, 195, 222);
  color: rgba(255, 255, 255);

  margin-left: 20px;
  margin-top: 40px;
  margin-bottom: 15px;
  border-right-width: 1px;
`;

// #endregion

const BookkeepingEntriesReporting: React.FC = () => {
  const { t } = useTranslation();

  // URL-based filters
  const { filters, dateFilter, setTerminalId, setDateFilter } = useBookkeepingEntriesReportFilters();

  // Fetch terminals using TanStack Query
  const { data: terminals = [] } = useTerminalsQuery();

  // Report download mutation
  const downloadMutation = useDownloadBookkeepingEntriesReportMutation();

  const isLoading = downloadMutation.isPending;

  const terminalOptions = useMemo(() => terminals.map((i) => ({ value: i.id, label: i.name })), [terminals]);
  const selectedTerminal = useMemo(
    () => terminalOptions.find((i) => i.value === filters.terminalId),
    [terminalOptions, filters.terminalId]
  );

  const terminalChanged = useCallback(
    (newTerminalId: string | null) => {
      setTerminalId(newTerminalId);
    },
    [setTerminalId]
  );

  const createReport = useCallback(() => {
    downloadMutation.mutate({
      terminalId: filters.terminalId,
      from: filters.startDate,
      to: filters.endDate,
    });
  }, [downloadMutation, filters]);

  const isReportCreationDisabled = !filters.startDate || !filters.endDate;

  return (
    <>
      <NavBar />
      <FilterContainer>
        <Columns>
          <DateFilterControlled value={dateFilter} onChange={setDateFilter} />
          <Column>
            <InputLabel htmlFor="terminalId">{t('entry.terminal')}</InputLabel>
            <Select
              name="terminalId"
              placeholder={t('select_terminal')}
              value={selectedTerminal ? selectedTerminal : { value: '', label: '' }}
              onChange={(i: { value: string } | null, j: { action: string }) =>
                terminalChanged(j.action === 'clear' ? null : i?.value ?? null)
              }
              options={terminalOptions}
            />
          </Column>
          {isLoading && <FormLoader title={t('reports.loading')} />}
          <CreateReportButton onClick={createReport} disabled={isReportCreationDisabled}>
            {t('reports.create_report')}
          </CreateReportButton>
        </Columns>
      </FilterContainer>
    </>
  );
};

export default BookkeepingEntriesReporting;
